import { Helmet } from "react-helmet-async";
import { INDEX_ROBOTS, NOINDEX_ROBOTS } from "@/app/_seo/SeoHelmet";
import { absolutePublicUrl, DEFAULT_OG_IMAGE_URL } from "@/app/_utils/constants";
import { toPublicAssetUrl } from "@/app/_utils/showOgImage";

type FestivalPageHelmetProps = {
  slug?: string | null;
  title: string;
  description?: string | null;
  imgUrl?: string | null;
  /** Use `title` as the full tab and link title, without the ComedyUO prefix. */
  exactTitle?: boolean;
  /** When true, ask search engines not to index (unpublished / missing festivals). */
  noIndex?: boolean;
};

function metaDescription(raw: string | null | undefined, fallback: string): string {
  const text = String(raw ?? "")
    .replace(/\s+/g, " ")
    .trim();
  const source = text || fallback;
  return source.length > 280 ? `${source.slice(0, 277).trimEnd()}…` : source;
}

export function FestivalPageHelmet({
  slug,
  title,
  description,
  imgUrl,
  exactTitle = false,
  noIndex = false,
}: FestivalPageHelmetProps) {
  const desc = metaDescription(description, title);
  const sourceImage = toPublicAssetUrl(imgUrl);
  const ogImage = sourceImage
    ? sourceImage.startsWith("/")
      ? absolutePublicUrl(sourceImage)
      : sourceImage
    : DEFAULT_OG_IMAGE_URL;
  const linkTitle = exactTitle
    ? title.trim() || "Festival"
    : `ComedyUO – ${title.trim() || "Festival"}`;
  const canonicalPath = slug ? `/festivals/${slug}` : undefined;
  const canonicalUrl = canonicalPath ? absolutePublicUrl(canonicalPath) : undefined;

  return (
    <Helmet>
      <title>{linkTitle}</title>
      <meta name="description" content={desc} />
      <meta property="og:title" content={linkTitle} />
      <meta property="og:description" content={desc} />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:site_name" content="Underground Overground Comedy" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={linkTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
      <meta name="robots" content={noIndex ? NOINDEX_ROBOTS : INDEX_ROBOTS} />
    </Helmet>
  );
}
